import { EllipsisVertical, Search, Video } from "lucide-react";
import { useMemo, useState } from "react";
import { MockAvatar } from "@/src/common/components/app-mock/avatar";
import { MOCK_COMMUNITY, MOCK_PEOPLE, type MockPerson, ROLE_CHIP_LABEL } from "@/src/common/components/app-mock/mock-community";
import { MOCK_TOKENS, MockChip, MockSelect } from "@/src/common/components/app-mock/primitives";

function RoleChip({ person }: { person: MockPerson }) {
	const label = ROLE_CHIP_LABEL[person.role];
	if (!label) return null;
	return (
		<MockChip color={person.role === "moderator" ? "default" : "accent"} size="xs">
			{label}
		</MockChip>
	);
}

function LastActive({ person }: { person: MockPerson }) {
	return (
		<span className="inline-flex items-center gap-1.5 text-[11px] text-neutral-600">
			{person.presence === "online" ? <span className="size-1.5 rounded-full" style={{ background: MOCK_TOKENS.success }} /> : null}
			{person.lastActive}
		</span>
	);
}

interface WebMembersScreenProps {
	interactive?: boolean;
	maxRows?: number;
}

export function WebMembersScreen({ interactive, maxRows }: WebMembersScreenProps) {
	const [query, setQuery] = useState("");
	const rows = useMemo(() => {
		const lowered = query.trim().toLowerCase();
		const filtered = lowered ? MOCK_PEOPLE.filter((person) => person.name.toLowerCase().includes(lowered)) : MOCK_PEOPLE;
		return maxRows ? filtered.slice(0, maxRows) : filtered;
	}, [query, maxRows]);

	return (
		<div className="space-y-3" data-mock="members">
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
				<h2 className="text-sm font-semibold text-neutral-900">Member List ({MOCK_COMMUNITY.memberCountLabel})</h2>
				<div className="flex items-center gap-2">
					<MockSelect value="All groups" />
					<div
						className="flex h-7 w-40 items-center gap-2 rounded-xl bg-white px-2.5 text-[11px]"
						style={{ boxShadow: MOCK_TOKENS.surfaceShadow }}
					>
						<Search className="size-3.5 shrink-0 text-neutral-500" />
						{interactive ? (
							<input
								type="text"
								value={query}
								onChange={(event) => setQuery(event.target.value)}
								placeholder="Search Members"
								aria-label="Search members"
								className="min-w-0 flex-1 bg-transparent text-neutral-900 placeholder:text-neutral-500 focus:outline-none"
							/>
						) : (
							<span className="text-neutral-500">Search Members</span>
						)}
					</div>
				</div>
			</div>

			<div className="hidden overflow-hidden rounded-xl bg-white sm:block" style={{ boxShadow: MOCK_TOKENS.surfaceShadow }}>
				<table className="w-full table-fixed border-collapse text-left">
					<thead>
						<tr
							className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500"
							style={{ background: MOCK_TOKENS.surfaceSecondary }}
						>
							<th className="w-8 px-3 py-2">
								<span className="block size-3 rounded border border-neutral-400" aria-hidden="true" />
							</th>
							<th className="w-[38%] px-2 py-2">Member Name</th>
							<th className="w-[22%] px-2 py-2">Group</th>
							<th className="w-[22%] px-2 py-2">Last Active</th>
							<th className="px-3 py-2 text-right">Actions</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((person) => (
							<tr
								key={person.id}
								className="group border-t hover:bg-neutral-50"
								style={{ borderColor: MOCK_TOKENS.separator }}
								data-mock="member-row"
							>
								<td className="px-3 py-2">
									<span className="block size-3 rounded border border-neutral-300" aria-hidden="true" />
								</td>
								<td className="px-2 py-2">
									<span className="flex min-w-0 items-center gap-2">
										<MockAvatar person={person} size={26} />
										<span className="truncate text-[11px] font-medium text-neutral-900">{person.name}</span>
										<RoleChip person={person} />
									</span>
								</td>
								<td className="truncate px-2 py-2 text-[10px] text-neutral-600">
									{person.groups.slice(0, 2).join(", ")}
									{person.groups.length > 2 ? <span className="text-neutral-400"> +{person.groups.length - 2}</span> : null}
								</td>
								<td className="px-2 py-2">
									<LastActive person={person} />
								</td>
								<td className="px-3 py-2">
									<span className="flex items-center justify-end gap-1 text-neutral-500" aria-hidden="true">
										<span className="inline-flex items-center gap-1 rounded-lg px-1.5 py-0.5 text-[10px] opacity-0 transition group-hover:opacity-100">
											<Video className="size-3" />
											Schedule
										</span>
										<EllipsisVertical className="size-3.5" />
									</span>
								</td>
							</tr>
						))}
						{rows.length === 0 ? (
							<tr>
								<td colSpan={5} className="py-6 text-center text-[11px] text-neutral-500">
									No members found
								</td>
							</tr>
						) : null}
					</tbody>
				</table>
			</div>

			<ul className="space-y-1.5 sm:hidden">
				{rows.map((person) => (
					<li
						key={person.id}
						className="flex items-center gap-2 rounded-lg border bg-white p-2"
						style={{ borderColor: MOCK_TOKENS.separator }}
					>
						<MockAvatar person={person} size={30} presence={person.presence === "online" ? "online" : undefined} />
						<div className="min-w-0 flex-1">
							<span className="flex items-center gap-1.5">
								<span className="truncate text-[11px] font-medium text-neutral-900">{person.name}</span>
								<RoleChip person={person} />
							</span>
							<p className="truncate text-[10px] text-neutral-500">{person.groups.join(", ")}</p>
						</div>
						<span className="text-[10px] text-neutral-500">{person.lastActive}</span>
					</li>
				))}
			</ul>

			<div className="flex items-center justify-between text-[10px] text-neutral-500" aria-hidden="true">
				<span>Showing 1-8 of {MOCK_COMMUNITY.memberCount.toLocaleString("en-US")}</span>
				<span className="flex items-center gap-1">
					{["1", "2", "3", "…", "161"].map((page, index) => (
						<span
							key={page}
							className="inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1"
							style={index === 0 ? { background: "#171717", color: "#fff" } : undefined}
						>
							{page}
						</span>
					))}
				</span>
			</div>
		</div>
	);
}
